import clsx from 'clsx'
import { motion } from 'motion/react'
import {
  type MouseEvent,
  type ReactElement,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { Label } from '~/components/Form/Input/Label'
import {
  container,
  containerOn,
  containerSmall,
  handle,
  handleOn,
  isDisabled,
  toggleLabel
} from './Toggle.css'

type ToggleProps = {
  defaultState: boolean
  onChange?: (checked: boolean) => void
  label?: string
  disabled?: boolean
  inputSize?: 'small'
}

export const Toggle = ({
  defaultState = false,
  onChange,
  label,
  disabled = false,
  inputSize
}: ToggleProps): ReactElement => {
  const [isOn, setIsOn] = useState(defaultState)
  const [handleOffset, setHandleOffset] = useState(0)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const handleRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    setIsOn(defaultState)
  }, [defaultState])

  useLayoutEffect(() => {
    const updateHandleOffset = () => {
      const toggleWidth = toggleRef.current?.getBoundingClientRect().width ?? 0
      const handleWidth = handleRef.current?.getBoundingClientRect().width ?? 0
      if (toggleWidth > 0 && handleWidth > 0) {
        setHandleOffset(toggleWidth - (handleWidth + 6))
      }
    }

    updateHandleOffset()

    const toggleElement = toggleRef.current
    if (!toggleElement) {
      return
    }

    const observer = new ResizeObserver(updateHandleOffset)
    observer.observe(toggleElement)
    return () => observer.disconnect()
  }, [])

  const toggleSwitch = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (disabled) return
    setIsOn(!isOn)
    onChange?.(!isOn)
  }

  const Element = (
    <button
      type="button"
      className={clsx(
        container,
        isOn ? containerOn : null,
        disabled ? isDisabled : null,
        inputSize === 'small' ? containerSmall : null
      )}
      ref={toggleRef}
      disabled={disabled}
      aria-disabled={disabled}
      onClick={toggleSwitch}
    >
      <motion.div
        className={clsx(handle, isOn ? handleOn : null)}
        animate={{
          x: isOn ? handleOffset : 0
        }}
        transition={{
          type: 'spring',
          visualDuration: 0.2,
          bounce: 0.2
        }}
        ref={handleRef}
      />
    </button>
  )

  if (label) {
    return (
      <Label className={toggleLabel} inputSize={inputSize}>
        {Element}
        {label}
      </Label>
    )
  }

  return Element
}
